'use client'

import { PartnerPayoutScheduleType } from '@/lib/api/common/interfaces/partner.interface'
import { MdPayments } from 'react-icons/md'

export interface PayoutScheduleValue {
    payoutScheduleType: PartnerPayoutScheduleType
    payoutFixedDay: string
    payoutDaysAfterAppointment: string
}

/**
 * Para onde o dinheiro vai. Hoje só o **parceiro** tem esses campos no schema;
 * profissional e fornecedor recebem por conta bancária vinculada ao lançamento (ver
 * a precedência em `TransferService`). Por isso o bloco é opcional: exibir campos que
 * o pagamento não lê seria pior que não exibir — o operador preencheria e a
 * transferência continuaria falhando.
 */
export interface PayoutTransferValue {
    pixKey: string
    pixKeyType: string
    bankCode: string
    bankAgency: string
    bankAccount: string
    bankAccountDigit: string
    bankAccountType: string
}

interface PayoutScheduleSectionProps {
    value: PayoutScheduleValue
    onChange: (next: PayoutScheduleValue) => void
    title?: string
    description?: string
    /** Exibe PIX e dados bancários. Omitido, a seção mostra só o ciclo. */
    transfer?: {
        value: PayoutTransferValue
        onChange: (next: PayoutTransferValue) => void
    }
}

export function PayoutScheduleSection({
    value,
    onChange,
    title = 'Repasse de Valores',
    description = 'Defina como os valores serão repassados.',
    transfer,
}: PayoutScheduleSectionProps) {
    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChange({
            ...value,
            payoutScheduleType: e.target.value as PartnerPayoutScheduleType,
        })
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
            <h2 className="text-lg font-semibold text-base-content mb-1 flex items-center gap-2">
                <MdPayments className="text-xl" />
                {title}
            </h2>
            <p className="text-sm text-base-content/60 mb-4">{description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control w-full md:col-span-2">
                    <label className="label">
                        <span className="label-text">Modelo de repasse</span>
                    </label>
                    <select
                        className="select select-bordered w-full"
                        value={value.payoutScheduleType}
                        onChange={handleTypeChange}
                    >
                        <option value="OPEN">Em aberto (sem data definida)</option>
                        <option value="FIXED_DAY">Dia fixo do mês</option>
                        {/* Obsoleto: não é mais oferecido. A opção só aparece para um
                            cadastro que JÁ está nela — sem isso o select abriria vazio
                            e o primeiro salvamento trocaria o ciclo sem ninguém pedir. */}
                        {value.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT' && (
                            <option value="DAYS_AFTER_APPOINTMENT">
                                Dias após a data do agendamento (obsoleto)
                            </option>
                        )}
                    </select>
                </div>

                {value.payoutScheduleType === 'FIXED_DAY' && (
                    <div className="form-control w-full md:col-span-2">
                        <label className="label">
                            <span className="label-text">Dia do mês para repasse</span>
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={31}
                            placeholder="Ex: 10"
                            className="input input-bordered w-full"
                            value={value.payoutFixedDay}
                            onChange={e =>
                                onChange({ ...value, payoutFixedDay: e.target.value })
                            }
                        />
                        <label className="label">
                            <span className="label-text-alt text-base-content/60">
                                Com o dia preenchido, a conta a pagar deste repasse é
                                gerada automaticamente nesse dia, somando o que está em
                                aberto. Em meses mais curtos cai no último dia (29, 30 e
                                31 viram 28 em fevereiro).
                            </span>
                        </label>
                    </div>
                )}

                {value.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT' && (
                    <div className="form-control w-full md:col-span-2">
                        <label className="label">
                            <span className="label-text">
                                Quantidade de dias após o agendamento
                            </span>
                        </label>
                        <input
                            type="number"
                            min={1}
                            placeholder="Ex: 15"
                            className="input input-bordered w-full"
                            value={value.payoutDaysAfterAppointment}
                            onChange={e =>
                                onChange({ ...value, payoutDaysAfterAppointment: e.target.value })
                            }
                        />
                        <label className="label">
                            <span className="label-text-alt text-warning">
                                Modelo obsoleto. Ele define o vencimento de cada repasse,
                                mas não gera a conta a pagar automaticamente — para isso,
                                use dia fixo do mês.
                            </span>
                        </label>
                    </div>
                )}

                {value.payoutScheduleType === 'OPEN' && (
                    <div className="md:col-span-2">
                        <div className="alert">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                className="stroke-current shrink-0 w-5 h-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <span className="text-sm">
                                Repasse em aberto, sem data definida (padrão). O
                                agrupamento das contas a pagar continua sendo feito à
                                mão.
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {transfer && (
                <>
                    {/* A transferência pela conta digital resolve o destino nesta
                        ordem: chave PIX primeiro; sem ela, banco + agência + conta (o
                        TED do Asaas exige o dígito). Sem nenhum dos dois, aprovar a
                        conta a pagar é recusado. */}
                    <div className="divider text-sm text-base-content/50">
                        Dados para transferência
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Chave PIX</span>
                            </label>
                            <input
                                type="text"
                                placeholder="CPF/CNPJ, e-mail, telefone ou chave aleatória"
                                className="input input-bordered w-full"
                                value={transfer.value.pixKey}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        pixKey: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Havendo chave PIX, é ela que será usada — a
                                    transferência sai por PIX, não por TED.
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Tipo da chave</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={transfer.value.pixKeyType}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        pixKeyType: e.target.value,
                                    })
                                }
                            >
                                <option value="">Detectar automaticamente</option>
                                <option value="CPF">CPF</option>
                                <option value="CNPJ">CNPJ</option>
                                <option value="EMAIL">E-mail</option>
                                <option value="PHONE">Telefone</option>
                                <option value="EVP">Chave aleatória</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Banco (código)</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: 001, 341, 260"
                                className="input input-bordered w-full"
                                value={transfer.value.bankCode}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        bankCode: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Agência</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Sem dígito"
                                className="input input-bordered w-full"
                                value={transfer.value.bankAgency}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        bankAgency: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Conta</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Número da conta"
                                className="input input-bordered w-full"
                                value={transfer.value.bankAccount}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        bankAccount: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Dígito da conta</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: 0"
                                className="input input-bordered w-full"
                                value={transfer.value.bankAccountDigit}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        bankAccountDigit: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Obrigatório para TED (transferência sem chave PIX).
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Tipo de conta</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={transfer.value.bankAccountType || 'CONTA_CORRENTE'}
                                onChange={e =>
                                    transfer.onChange({
                                        ...transfer.value,
                                        bankAccountType: e.target.value,
                                    })
                                }
                            >
                                <option value="CONTA_CORRENTE">Conta corrente</option>
                                <option value="CONTA_POUPANCA">Conta poupança</option>
                            </select>
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Exigido pelo provedor no TED.
                                </span>
                            </label>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export function validatePayoutScheduleClient(value: PayoutScheduleValue): string | null {
    if (value.payoutScheduleType === 'FIXED_DAY') {
        const day = parseInt(value.payoutFixedDay, 10)
        if (!day || day < 1 || day > 31) {
            return 'Informe um dia do mês válido (1 a 31) para o repasse'
        }
    }
    if (value.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT') {
        const days = parseInt(value.payoutDaysAfterAppointment, 10)
        if (!days || days < 1) {
            return 'Informe a quantidade de dias após o agendamento para o repasse'
        }
    }
    return null
}

/**
 * Dados de transferência para o payload da API.
 *
 * Campo vazio vira `null`, não `''`: string vazia gravada em `pixKey` faria
 * `canTransferTo` considerar que há chave, e a transferência sairia com chave em
 * branco para o provedor.
 */
export function payoutTransferToPayload(value: PayoutTransferValue) {
    const clean = (v: string) => (v.trim() ? v.trim() : null)
    return {
        pixKey: clean(value.pixKey),
        pixKeyType: clean(value.pixKeyType),
        bankCode: clean(value.bankCode),
        bankAgency: clean(value.bankAgency),
        bankAccount: clean(value.bankAccount),
        bankAccountDigit: clean(value.bankAccountDigit),
        bankAccountType: clean(value.bankAccountType) ?? 'CONTA_CORRENTE',
    }
}

export function payoutScheduleToPayload(value: PayoutScheduleValue) {
    return {
        payoutScheduleType: value.payoutScheduleType,
        payoutFixedDay:
            value.payoutScheduleType === 'FIXED_DAY' && value.payoutFixedDay
                ? parseInt(value.payoutFixedDay, 10)
                : null,
        payoutDaysAfterAppointment:
            value.payoutScheduleType === 'DAYS_AFTER_APPOINTMENT' && value.payoutDaysAfterAppointment
                ? parseInt(value.payoutDaysAfterAppointment, 10)
                : null,
    }
}
